import * as React from 'react'

import 'modules/employees/pages/ImportContractsPage/components/ImportTable/Content'
import type { ImportedContract } from 'modules/employees/pages/ImportContractsPage/components/ImportTable/Content/types'

interface SetContractsProps {
  contracts: ImportedContract[]
  attributes?: string[]
  customFields?: string[]
  isOverlimited?: boolean
  currentSeats?: number
  newContracts?: number
  maxContractsAllowedImport?: number
}

interface ContractImporterState {
  files: File[]
  contracts: ImportedContract[]
  token: string | null | undefined
  mode: string | null | undefined
  readyContracts: ImportedContract[]
  attributes: string[]
  customFields: string[]
  hasContracts: boolean
  hasFile: boolean
  currentSeats: number
  newContracts: number
  isOverlimited: boolean
  maxContractsAllowedImport: number
}

export interface ContractImporterContextProps extends ContractImporterState {
  setFiles: (files: File[]) => void
  setContracts: (arg0: SetContractsProps) => void
  setToken: (token: string) => void
  setMode: (mode: string) => void
}

type SetFilesAction = {
  type: 'set_files'
  payload: {
    files: File[]
  }
}
type SetContractsAction = {
  type: 'set_contracts'
  payload: {
    contracts: ImportedContract[]
    attributes: string[]
    customFields: string[]
    currentSeats: number
    newContracts: number
    isOverlimited: boolean
    maxContractsAllowedImport: number
  }
}
type SetTokenAction = {
  type: 'set_token'
  payload: {
    token: string
  }
}
type SetModeAction = {
  type: 'set_mode'
  payload: {
    mode: string
  }
}

type ContractImporterAction = SetFilesAction | SetContractsAction | SetTokenAction | SetModeAction

const ContractImporterContext = React.createContext<ContractImporterContextProps | null>(null)

const initialState = {
  files: [],
  contracts: [],
  token: null,
  mode: null,
  readyContracts: [],
  attributes: [],
  customFields: [],
  hasContracts: false,
  hasFile: false,
  currentSeats: 0,
  newContracts: 0,
  isOverlimited: false,
  maxContractsAllowedImport: 0,
}

const reducer = (state: ContractImporterState, action: ContractImporterAction) => {
  const { type, payload } = action

  switch (type) {
    case 'set_files': {
      const files = payload.files || []

      return { ...state, files, hasFile: files.length > 0 }
    }
    case 'set_contracts': {
      const contracts = payload.contracts

      return {
        ...state,
        contracts,
        attributes: payload.attributes,
        customFields: payload.customFields,
        hasContracts: contracts.length > 0,
        currentSeats: payload.currentSeats,
        newContracts: payload.newContracts,
        isOverlimited: payload.isOverlimited,
        maxContractsAllowedImport: payload.maxContractsAllowedImport,
        readyContracts: contracts.filter((contract) => contract.status === 'ready'),
      }
    }
    case 'set_token': {
      const token = payload.token || null

      return { ...state, token }
    }
    case 'set_mode': {
      const mode = payload.mode || null

      return { ...state, mode }
    }

    default:
      throw new Error(`Type of action: ${type} not found`)
  }
}

export const ContractsImporterProvider = ({ children }: React.PropsWithChildren) => {
  const [state, dispatch] = React.useReducer(reducer, initialState)

  const setFiles = (files: File[]) => {
    dispatch({
      type: 'set_files',
      payload: {
        files,
      },
    })
  }

  const setContracts = ({
    contracts,
    attributes = [],
    customFields = [],
    isOverlimited = false,
    currentSeats = 0,
    newContracts = 0,
    maxContractsAllowedImport = 0,
  }: SetContractsProps) => {
    dispatch({
      type: 'set_contracts',
      payload: {
        contracts,
        attributes,
        customFields,
        isOverlimited,
        currentSeats,
        newContracts,
        maxContractsAllowedImport,
      },
    })
  }

  const setToken = (token: string) => {
    dispatch({
      type: 'set_token',
      payload: {
        token,
      },
    })
  }

  const setMode = (mode: string) => {
    dispatch({
      type: 'set_mode',
      payload: {
        mode,
      },
    })
  }

  return (
    <ContractImporterContext.Provider
      value={{ ...state, setFiles, setContracts, setToken, setMode }}
    >
      {children}
    </ContractImporterContext.Provider>
  )
}

export const useContractsImporter = () => {
  const storeContext = React.useContext(ContractImporterContext)

  if (!storeContext) {
    throw new Error('useContractsImporter must be used within a ContractImporterContext')
  }

  return storeContext
}
